// screens/OrderDetailScreen.js - Screen 9
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import {
  SyncBar, PhotoThumb, CommentThread,
  FlagChips, SectionHeader, EmptyState, ConfirmDialog, OrderSyncBadge
} from '../components/shared';
import PhotoViewer from '../components/PhotoViewer';
import OrderInfoBlock from '../components/OrderInfoBlock';
import QcApproval from '../components/QcApproval';
import { ComplaintLinesBlock, ComplaintSheet, ComplaintTimeline } from '../components/OrderComplaints';
import { loadComplaintTaxonomy } from '../hooks/useComplaintTaxonomy';
import { BottomNav } from './HomeScreen';
import api, { getErrorMessage } from '../utils/api';
import { deliverFile, isNativeShell, openExternalUrl } from '../utils/download';
import { withLocalQc } from '../utils/qcApproval';

export default function OrderDetailScreen() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSession, setSelectedSession] = useState(null);
  const [viewerPhoto, setViewerPhoto] = useState(null);
  const [activityOpen, setActivityOpen] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [savingFlags, setSavingFlags] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [orderBusy, setOrderBusy] = useState(false);
  const [editDetails, setEditDetails] = useState(null); // { order_number, product_name }
  const [savingDetails, setSavingDetails] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [docDragOver, setDocDragOver] = useState(false);
  const docInputRef = useRef();
  const [taxonomy, setTaxonomy] = useState(null);
  const [sheet, setSheet] = useState(null); // { line } while the complaint pop-up is open; line null = new

  const canReview = ['Reviewer', 'Supervisor', 'Admin', 'R&D'].includes(user?.role);
  const isAdmin = user?.role === 'Admin';

  useEffect(() => { loadOrder(); }, [id]);

  // The choice lists are only needed (and only allowed) where the server sent complaints.
  const hasComplaints = !!data?.complaints;
  useEffect(() => {
    if (hasComplaints) loadComplaintTaxonomy().then(setTaxonomy).catch(() => {});
  }, [hasComplaints]);

  // `quiet` refreshes in place: no spinner, and the chosen session stays selected.
  const loadOrder = async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const { data: d } = await api.get(`/api/orders/${id}`);
      setData({ ...d, order: withLocalQc(d.order) });
      if (!quiet) setSelectedSession(d.sessions?.[d.sessions.length - 1]?.id || null);
    } catch { toast.error('Failed to load order'); }
    finally { if (!quiet) setLoading(false); }
  };

  const handleComplaintSaved = () => {
    setSheet(null);
    return loadOrder(true);
  };

  const handleFlagChange = async (updated) => {
    if (!canReview) return;
    setSavingFlags(true);
    try {
      await api.patch(`/api/orders/${id}/flags`, {
        has_claim: updated.has_claim ? 1 : 0,
        used_for_marketing: updated.used_for_marketing ? 1 : 0,
        flagged_by_management: updated.flagged_by_management ? 1 : 0,
      });
      setData(d => ({ ...d, order: { ...d.order, ...updated } }));
    } catch { toast.error('Failed to update flags'); }
    finally { setSavingFlags(false); }
  };

  const handleToggleActive = async () => {
    setOrderBusy(true);
    try {
      const nextActive = data.order.active ? 0 : 1;
      await api.patch(`/api/orders/${id}`, { active: nextActive });
      toast.success(nextActive ? 'Order reactivated' : 'Order deactivated');
      if (!nextActive) navigate(-1);
      else setData(d => ({ ...d, order: { ...d.order, active: 1 } }));
    } catch (err) { toast.error(getErrorMessage(err)); }
    finally { setOrderBusy(false); }
  };

  const handleSaveDetails = async () => {
    const order_number = editDetails.order_number.trim();
    const product_name = editDetails.product_name.trim();
    if (!order_number || !product_name) { toast.error('Order number and product name are required'); return; }
    setSavingDetails(true);
    try {
      await api.patch(`/api/orders/${id}/details`, { order_number, product_name });
      setData(d => ({ ...d, order: { ...d.order, order_number, product_name } }));
      toast.success('Order updated');
      setEditDetails(null);
    } catch (err) { toast.error(getErrorMessage(err)); }
    finally { setSavingDetails(false); }
  };

  const uploadDocFiles = async (files) => {
    const list = Array.from(files || []).filter(Boolean);
    if (list.length === 0) return;
    setUploadingDoc(true);
    try {
      for (const file of list) {
        const fd = new FormData();
        fd.append('file', file);
        await api.post(`/api/orders/${id}/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 });
      }
      toast.success(list.length === 1 ? 'Uploaded' : `${list.length} files uploaded`);
      await loadOrder();
    } catch (err) { toast.error(getErrorMessage(err)); }
    finally { setUploadingDoc(false); }
  };

  const handleUploadDoc = (e) => {
    const files = e.target.files;
    uploadDocFiles(files);
    e.target.value = '';
  };

  const handleDocDrop = (e) => {
    e.preventDefault();
    setDocDragOver(false);
    uploadDocFiles(e.dataTransfer?.files);
  };

  const handleDeleteDoc = async (docId) => {
    const prev = data;
    setData(d => ({ ...d, documents: (d.documents || []).filter(x => x.id !== docId) }));
    try {
      await api.delete(`/api/orders/${id}/documents/${docId}`);
    } catch (err) { setData(prev); toast.error(getErrorMessage(err)); }
  };

  const handleDelete = async () => {
    setOrderBusy(true);
    setShowDelete(false);
    try {
      await api.delete(`/api/orders/${id}`);
      toast.success('Order deleted');
      navigate('/search', { replace: true });
    } catch (err) { toast.error(getErrorMessage(err)); setOrderBusy(false); }
  };

  const handleNewComment = (comment) => {
    setData(d => ({ ...d, comments: [...(d.comments || []), comment] }));
  };

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="spinner spinner-lg" />
    </div>
  );

  if (!data) return null;

  const { order, sessions, photos, comments, activity, documents = [], info, complaints } = data;
  const sessionPhotos = selectedSession
    ? photos.filter(p => p.session_id === selectedSession)
    : photos;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', minHeight: '100dvh' }}>
      <SyncBar />
      <div className="page-header">
        <button className="btn btn-ghost btn-icon" onClick={() => navigate(-1)}>←</button>
        <div style={{ textAlign: 'center', flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: '0.9375rem', color: 'var(--accent-primary)' }}>
            {order.order_number}
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {order.product_name}
          </div>
        </div>
        <button className="btn btn-secondary btn-sm" onClick={() => setShowExport(true)}>↓ Export</button>
      </div>

      <div className="page-content">
        <div className="desktop-two-col">
          {/* LEFT: Photos + sessions */}
          <div>
            <OrderInfoBlock orderId={id} info={info} canEdit={!!complaints?.can_edit}
              onRegister={complaints?.can_edit && taxonomy ? () => setSheet({ line: null }) : null}
              onInfoSaved={(next) => setData(d => ({ ...d, info: next }))} />

            {/* Order meta */}
            <div className="card" style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <div>
                  <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
                    {order.site_name} · {new Date(order.created_at).toLocaleDateString()} · by {order.creator_name}
                  </div>
                  <div style={{ marginTop: 6 }}>
                    <OrderSyncBadge order={order} showTime />
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <button className="btn btn-ghost btn-sm" title="Correct order number / product name"
                    onClick={() => setEditDetails({ order_number: order.order_number, product_name: order.product_name })}>
                    ✎ Correct
                  </button>
                  <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>
                    {sessions.length} session{sessions.length !== 1 ? 's' : ''}
                  </div>
                </div>
              </div>
              {order.factory && (
                <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginBottom: 8 }}>
                  Factory: <strong>{order.factory}</strong>
                </div>
              )}

              {/* Flags */}
              <div style={{ marginBottom: 12 }}>
                <div className="form-label" style={{ marginBottom: 8 }}>Flags {savingFlags && <span style={{ color: 'var(--text-muted)' }}>(saving...)</span>}</div>
                <FlagChips order={order} onChange={handleFlagChange} readOnly={!canReview} />
              </div>

              {/* Admin order management — deactivate (hide) or delete test orders */}
              {isAdmin && (
                <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border-subtle)', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button className="btn btn-secondary btn-sm" disabled={orderBusy} onClick={handleToggleActive}>
                    {order.active ? '🚫 Deactivate' : '✓ Reactivate'}
                  </button>
                  <button className="btn btn-ghost btn-sm" style={{ color: 'var(--accent-danger)' }}
                    disabled={orderBusy} onClick={() => setShowDelete(true)}>
                    🗑 Delete order
                  </button>
                </div>
              )}
            </div>

            {/* QC approval: button for Supervisor+, status for everyone once approved */}
            <QcApproval user={user} order={order} photos={photos}
              onApproved={(done) => setData(d => ({ ...d, order: { ...d.order, ...done } }))} />

            {/* Session selector */}
            {sessions.length > 1 && (
              <div style={{ marginBottom: 16 }}>
                <div className="form-label" style={{ marginBottom: 8 }}>Session</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button
                    className={`btn btn-sm ${!selectedSession ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => setSelectedSession(null)}>
                    All sessions
                  </button>
                  {sessions.map(s => (
                    <button key={s.id}
                      className={`btn btn-sm ${selectedSession === s.id ? 'btn-primary' : 'btn-secondary'}`}
                      onClick={() => setSelectedSession(s.id)}>
                      Session {s.session_number}
                      <span style={{ marginLeft: 4, opacity: 0.7 }}>· {s.photographer_name?.split(' ')[0]}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Photo grid */}
            <SectionHeader title={`Photos (${sessionPhotos.length})`} />
            {sessionPhotos.length === 0 ? (
              <EmptyState icon="📷" title="No photos" message="No photos in this session" />
            ) : (
              <div className="photo-grid">
                {sessionPhotos.map(p => (
                  <PhotoThumb key={p.id} photo={p} onClick={() => setViewerPhoto(p)} />
                ))}
              </div>
            )}

            {/* Uploads — own photos / documents (PDF) attached to this order.
                The whole section is a drop target: dragging files anywhere onto
                it uploads them, no file picker needed. */}
            <div
              style={{
                marginTop: 20,
                ...(docDragOver ? { outline: '2px dashed var(--accent-primary)', outlineOffset: 6, borderRadius: 8 } : {}),
              }}
              onDragOver={(e) => { e.preventDefault(); setDocDragOver(true); }}
              onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDocDragOver(false); }}
              onDrop={handleDocDrop}
            >
              <SectionHeader
                title={`Uploads (${documents.length})`}
                action={
                  <button className="btn btn-secondary btn-sm" disabled={uploadingDoc}
                    onClick={() => docInputRef.current?.click()}>
                    {uploadingDoc ? 'Uploading…' : '⤓ Upload file'}
                  </button>
                }
              />
              <input ref={docInputRef} type="file" multiple accept="image/*,application/pdf" onChange={handleUploadDoc} style={{ display: 'none' }} />
              {documents.length === 0 ? (
                <div style={{ color: 'var(--text-muted)', fontSize: '0.8125rem', padding: '4px 0' }}>
                  No uploads yet. Add your own photos or PDF documents to this order — or drag & drop files here.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {documents.map(doc => (
                    <div key={doc.id} className="card" style={{ padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontSize: '1.25rem' }}>{doc.kind === 'photo' ? '🖼' : doc.kind === 'pdf' ? '📄' : '📎'}</span>
                      <a href={doc.url} target="_blank" rel="noopener noreferrer" style={{ flex: 1, minWidth: 0, color: 'var(--accent-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.875rem' }}>
                        {doc.filename}
                      </a>
                      <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', flexShrink: 0 }}>
                        {doc.uploaded_by_name?.split(' ')[0] || ''}{doc.uploaded_at ? ` · ${new Date(doc.uploaded_at).toLocaleDateString()}` : ''}
                      </span>
                      <button className="btn btn-ghost btn-sm" style={{ color: 'var(--accent-danger)', flexShrink: 0 }}
                        onClick={() => handleDeleteDoc(doc.id)}>✕</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Complaint lines: reviewers, supervisors and admins only (the server omits the block otherwise) */}
            {complaints && (
              <ComplaintLinesBlock block={complaints} taxonomy={taxonomy}
                onAdd={() => setSheet({ line: null })}
                onEdit={(line) => setSheet({ line })}
                onChanged={() => loadOrder(true)} />
            )}
          </div>

          {/* RIGHT panel: Timeline + Comments + Activity */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {complaints && <ComplaintTimeline events={complaints.events} lines={complaints.lines} />}

            {/* Comments */}
            <div className="card">
              <SectionHeader title={`Comments (${comments.length})`} />
              <CommentThread
                comments={comments}
                orderId={id}
                onNewComment={handleNewComment}
              />
            </div>

            {/* Activity log (collapsed) */}
            <div className="card">
              <div className="accordion-header" onClick={() => setActivityOpen(o => !o)}>
                <div className="form-label" style={{ margin: 0 }}>Activity Log ({activity.length})</div>
                <div className={`accordion-arrow ${activityOpen ? 'open' : ''}`}>▾</div>
              </div>
              {activityOpen && (
                <div style={{ marginTop: 8 }}>
                  {activity.length === 0 ? (
                    <div style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>No activity</div>
                  ) : activity.map(a => (
                    <div key={a.id} className="activity-item">
                      <div className="activity-dot" />
                      <div>
                        <span style={{ fontWeight: 500 }}>{a.actor_name}</span>
                        {' '}
                        <span style={{ color: 'var(--text-muted)' }}>
                          {a.action.replace(/_/g, ' ')}
                        </span>
                        {a.detail && <span style={{ color: 'var(--text-muted)' }}> · {a.detail.substring(0, 60)}</span>}
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>
                          {new Date(a.created_at).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Photo viewer */}
      {viewerPhoto && (
        <PhotoViewer
          photo={viewerPhoto}
          photos={sessionPhotos.filter(p => !p.is_na)}
          onClose={() => setViewerPhoto(null)}
        />
      )}

      {sheet && complaints && taxonomy && (
        <ComplaintSheet order={order} block={complaints} line={sheet.line} taxonomy={taxonomy}
          onClose={() => setSheet(null)} onSaved={handleComplaintSaved} />
      )}

      {/* Export modal */}
      {showExport && (
        <ExportModal
          order={order}
          sessions={sessions}
          onClose={() => setShowExport(false)}
        />
      )}

      {/* Correct order number / product name */}
      {editDetails && (
        <div className="overlay" onClick={() => setEditDetails(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <h3 style={{ marginBottom: 16 }}>Correct order details</h3>
            <div className="form-group" style={{ marginBottom: 14 }}>
              <label className="form-label">Order number</label>
              <input type="text" className="input-field" value={editDetails.order_number}
                onChange={e => setEditDetails(v => ({ ...v, order_number: e.target.value }))} autoFocus />
            </div>
            <div className="form-group" style={{ marginBottom: 18 }}>
              <label className="form-label">Product name</label>
              <input type="text" className="input-field" value={editDetails.product_name}
                onChange={e => setEditDetails(v => ({ ...v, product_name: e.target.value }))} />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-secondary btn-full" onClick={() => setEditDetails(null)}>Cancel</button>
              <button className="btn btn-primary btn-full" onClick={handleSaveDetails} disabled={savingDetails}>
                {savingDetails ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={showDelete}
        title="⚠ Delete this order?"
        message="This permanently deletes the order and all its sessions, photos, annotations, comments and checklist responses. This cannot be undone. Use Deactivate instead if you only want to hide it."
        confirmLabel="Delete permanently"
        danger
        onConfirm={handleDelete}
        onCancel={() => setShowDelete(false)}
      />

      <BottomNav active="search" />
    </div>
  );
}

// ── Export Modal ──────────────────────────────────────────────────────────────
function ExportModal({ order, sessions, onClose }) {
  const toast = useToast();
  const [selectedSessions, setSelectedSessions] = useState(sessions.map(s => s.id));
  const [exportType, setExportType] = useState('customer-pdf');
  const [includeFlags, setIncludeFlags] = useState(true);
  const [includeChecklist, setIncludeChecklist] = useState(true);
  const [includeComments, setIncludeComments] = useState(true);
  const [includeAnnotations, setIncludeAnnotations] = useState(true);

  const [exporting, setExporting] = useState(false);
  const nativeShell = isNativeShell();

  const exportParams = () => new URLSearchParams({
    type: exportType,
    sessions: selectedSessions.join(','),
    flags: includeFlags ? '1' : '0',
    checklist: includeChecklist ? '1' : '0',
    comments: includeComments ? '1' : '0',
    annotations: includeAnnotations ? '1' : '0',
  });

  // `disposition` only matters in the shell: 'attachment' saves the file, 'inline' opens it
  // in the browser's own PDF viewer first.
  const handleExport = async (disposition = 'attachment') => {
    if (selectedSessions.length === 0) return;
    setExporting(true);
    try {
      // The shell's WebView cannot receive a blob download at all, so the server parks the
      // export in storage and we open the signed URL, which Capacitor forwards to the
      // system browser. See utils/download.js.
      if (nativeShell) {
        const params = exportParams();
        params.set('link', '1');
        params.set('disposition', disposition);
        const res = await api.get(`/api/export/order/${order.id}?${params}`, { timeout: 180000 });
        if (!res.data?.url) throw new Error('no download url');
        openExternalUrl(res.data.url);
        toast.success(disposition === 'inline' ? 'Opening in your browser' : 'Saving in your browser');
        onClose();
        return;
      }

      const res = await api.get(`/api/export/order/${order.id}?${exportParams()}`, { responseType: 'blob' });
      const ext = exportType === 'customer-zip' ? 'zip' : 'pdf';
      const outcome = await deliverFile(res.data, `${order.order_number}_${exportType}.${ext}`);
      if (outcome === 'dismissed') return;      // the user closed the share sheet — not a failure
      if (outcome === 'unsupported') {          // never claim a success that did not happen
        toast.error('This device cannot save the file here');
        return;
      }
      toast.success('Export ready');
      onClose();
    } catch (err) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const toggleSession = (id) => {
    setSelectedSessions(prev =>
      prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
    );
  };

  const estimatedSize = selectedSessions.length * 6 * 2.5; // ~2.5MB per photo avg

  return (
    <div className="overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <h3 style={{ marginBottom: 4 }}>Export Order</h3>
        <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginBottom: 20 }}>
          {order.order_number} · {order.product_name}
        </div>

        {/* Export type */}
        <div className="form-group" style={{ marginBottom: 16 }}>
          <label className="form-label">Export Type</label>
          {[
            { value: 'customer-pdf', label: '📄 Customer PDF', desc: 'Large photos, order number only, no internal data' },
            { value: 'customer-zip', label: '📦 Customer ZIP', desc: 'High-res JPEGs, named [Order]_[Step]_[Date]' },
            { value: 'internal-pdf', label: '📋 Internal PDF', desc: 'Full data: flags, checklist, comments, annotations' },
          ].map(t => (
            <div key={t.value} className={`card card-hover ${exportType === t.value ? 'active' : ''}`}
              style={{
                marginTop: 6, padding: '10px 14px', cursor: 'pointer',
                border: `1px solid ${exportType === t.value ? 'var(--accent-primary)' : 'var(--border-subtle)'}`,
                background: exportType === t.value ? 'rgba(79,142,247,0.08)' : 'var(--bg-card)'
              }}
              onClick={() => setExportType(t.value)}>
              <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{t.label}</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>{t.desc}</div>
            </div>
          ))}
        </div>

        {/* Sessions */}
        <div className="form-group" style={{ marginBottom: 16 }}>
          <label className="form-label">Sessions to include</label>
          {sessions.map(s => (
            <label key={s.id} className="toggle-wrap" style={{ marginTop: 8 }}>
              <input type="checkbox" checked={selectedSessions.includes(s.id)}
                onChange={() => toggleSession(s.id)} style={{ display: 'none' }} />
              <div className={`toggle ${selectedSessions.includes(s.id) ? 'on' : ''}`} />
              <span style={{ fontSize: '0.875rem' }}>Session {s.session_number} · {s.photographer_name}</span>
            </label>
          ))}
        </div>

        {/* Internal options */}
        {exportType === 'internal-pdf' && (
          <div className="form-group" style={{ marginBottom: 16 }}>
            <label className="form-label">Include in PDF</label>
            {[
              { key: 'flags', label: 'Flags', state: includeFlags, setter: setIncludeFlags },
              { key: 'checklist', label: 'Quality Checklist', state: includeChecklist, setter: setIncludeChecklist },
              { key: 'comments', label: 'Comments', state: includeComments, setter: setIncludeComments },
              { key: 'annotations', label: 'Photo Annotations', state: includeAnnotations, setter: setIncludeAnnotations },
            ].map(item => (
              <label key={item.key} className="toggle-wrap" style={{ marginTop: 8 }} onClick={() => item.setter(!item.state)}>
                <div className={`toggle ${item.state ? 'on' : ''}`} />
                <span style={{ fontSize: '0.875rem' }}>{item.label}</span>
              </label>
            ))}
          </div>
        )}

        {/* Size estimate */}
        <div style={{
          padding: '10px 14px', background: 'var(--bg-input)',
          borderRadius: 'var(--radius-md)', marginBottom: 20,
          fontSize: '0.8125rem', color: 'var(--text-muted)'
        }}>
          Estimated size: ~{estimatedSize.toFixed(0)} MB
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-secondary btn-full" onClick={onClose}>Cancel</button>
          {nativeShell && exportType !== 'customer-zip' && (
            <button className="btn btn-secondary btn-full" onClick={() => handleExport('inline')}
              disabled={selectedSessions.length === 0 || exporting}>
              View
            </button>
          )}
          <button className="btn btn-primary btn-full" onClick={() => handleExport('attachment')}
            disabled={selectedSessions.length === 0 || exporting}>
            {exporting ? 'Preparing…' : '↓ Download'}
          </button>
        </div>
      </div>
    </div>
  );
}
